import { ActivatedRoute } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import {debounceTime, distinctUntilChanged, startWith, switchMap} from 'rxjs/operators';
import { merge, of, Observable } from 'rxjs';

import {
  Image, Page, PageBlock, PageHelper, PageService, Document,
  DocumentService
} from '../../../common/services';
import { DialogData, DialogValidationComponent } from '../../../../common/widgets/general/dialog-validation';
import { IconComponent } from '../../common/icon-picker/icon-picker.component';

import { SelectDocModalComponent } from '../page-blocks/select-doc-modal/select-doc-modal.component';
import { environment } from '@Env/environment';
import {Individu, IndividuService} from '@Common/core/services';
import {rolesBdc} from '../../../common/enum/roles-bdc';
import {PubSubService} from '../../../common/services/pub-sub.service';

@Component({
  selector: 'p8-bdc-page-form',
  templateUrl: './page-form.component.html',
  styleUrls: ['./page-form.component.scss']
})
export class PageFormComponent implements OnInit {
  pages$: Observable<Page[]>;
  page: Page = null;
  pageParent: Page = null;

  pageForm: UntypedFormGroup;
  icon: string = null;
  body: PageBlock[] = [];

  documents: Document[] = [];
  images: Image[] = [];

  isOpenIcons = false;
  bdcApp = 'bdc/';

  individu: Individu = null;
  hasRightsManageGest = false;

  orders: number[] = [];

  isUpdated = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private pubSubSvc: PubSubService,
    private formBuilder: UntypedFormBuilder,
    private pageHelper: PageHelper,
    private pageService: PageService,
    private documentService: DocumentService,
    private individuService: IndividuService
  ) { }

  ngOnInit() {
    this.individuService.individu$.subscribe(ind => {
      this.individu = ind;
      this.hasRightsManageGest = this.individuService.hasRole(ind, rolesBdc.supergestionnaire) || this.individuService.hasRole(ind, rolesBdc.admin);
    });

    this.activatedRoute.params.subscribe(async params => {
      if ('id' in params) {
        const id = params['id'];
        this.isUpdated = true;

        this.pubSubSvc.publish('loading', true);

        const page = await this.getPage(id);
        const pageParent = await this.getParent(id);

        this.pubSubSvc.publish('loading', false);

        this.initializeData(page, pageParent);
      } else {
        this.isUpdated = false;
        this.createForm();
      }
    });
  }

  async getPage(id) {
    return await this.pageService.get(id).toPromise();
  }

  async getParent(id)  {
    return await this.pageService.getParent(id).toPromise();
  }

  /**
   * Init des choix d'ordre de la page / rubrique
   * On prend 2 X le nombre de pages de la rubrique
   * @private
   */
  _initSorting(pageParent: Page) {
    if (pageParent && pageParent.numchild !== null && pageParent.numchild > 0) {
      this.orders = [...Array.from(Array(pageParent.numchild * 2).keys())].map((x) => x + 1);
    }
  }

  initializeData(page: Page, pageParent) {
    this.page = page;
    this.pageParent = pageParent;

    this._initSorting(pageParent);

    this.createForm(page.title, pageParent, page.order);

    this.icon = page.icon;
    this.body = ('body' in page.content) ? page.content.body : [];

    if (page.documents_obj && page.documents_obj.length > 0) {
      this.initChipsDocs(page.documents_obj);
    }

    if (page.images_obj && page.images_obj.length > 0) {
      page.images_obj.forEach((img: Image) => {
        this.images.push(img);
      });
    }

    /** la racine n'est pas modifiable **/
    if (page.path === '0001' && page.depth === 1) {
      this.pageForm.disable();
    }
  }

  getSlug() {
    return this.pageHelper.getSlug(this.page);
  }

  createForm(title: string = null, parent: Page = null, order = null) {
    this.pageForm = this.formBuilder.group({
      title: [title, Validators.required],
      parent: [parent, Validators.required],
      order: [order ? order : '0']
    });

    // It downloads the list of pages that match search criteria every time the input value changes
    // I use Observable.of('') as a way to lunch the download for the first time, before any input
    this.pages$ = merge(this.pageForm.controls.parent.valueChanges, of('')).pipe(
      // startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => this.pageService.list(0, 0, value)));
  }

  /**
   * Sélection d'une page parente / rubrique
   * @param parent
   */
  onSelect(parent: MatOptionSelectionChange) {
    if (parent.isUserInput) {
      // if (! this.isUpdated) {
        this.orders = [];
        this.pageForm.controls['order'].patchValue(0);
        this.pageParent = parent.source.value;
        this._initSorting(this.pageParent);
        console.log(this.pageParent);
      // }
    }
  }

  displayPageName(page: Page): string {
    return (page) ? page.title : '';
  }

  deleteIcon(event) {
    this.icon = null;
  }

  openIconsDialog() {
    setTimeout(() => this.isOpenIcons = true);

    const modalIcon = this.dialog.open(IconComponent);
    modalIcon.updateSize('550px', '650px');
    modalIcon.afterClosed().subscribe(data => {
      if (data) {
        this.icon = data.label;
      }
    });
    modalIcon.afterOpened().subscribe(() => setTimeout(() => this.isOpenIcons = false));
  }

  onImageBlock(imgBlock) {
    if (imgBlock && imgBlock.action === 'add') {
      console.log('add image ', imgBlock);
      this.images.push(imgBlock.image);
    }

    if (imgBlock && imgBlock.action === 'del') {
      const idx = this.images.findIndex((d) => {
        return d.id === imgBlock.block.value.imageId;
      });

      if (idx > -1) {
        console.log('delete image ', imgBlock);
        this.images.splice(idx, 1);
      }
    }
  }

  addBlock($event: any) {
    const index = ('index' in $event) ? $event.index : 0;
    const block = this.pageHelper.newBlock($event.type);
    if (block) {
      this.body.splice(index, 0, this.pageHelper.newBlock($event.type));
    }
  }

  switchOnOffLine(page: Page) {
    page.is_published = ! page.is_published;
  }

  switchToFavourite(page: Page) {
    page.is_favourite = ! page.is_favourite;
  }

  /**
   * Gestion des documents
   *
   */

  initChipsDocs(docs: Document[]) {
    docs.forEach((doc: Document) => {
      this.documents.push(doc);
    });
  }

  chipsToDocumentsPage(page: Page) {
    page.documents = [];
    this.documents.forEach((doc: Document) => {
      page.documents.push(doc.id);
    });
  }

  imagesToImagesPage(page: Page) {
    page.images = [];
    this.images.forEach((img: Image) => {
      page.images.push(img.id);
    });
  }

  openDocuments() {
    const modal = this.dialog.open(SelectDocModalComponent, );
    modal.updatePosition({top: '50px'});
    modal.updateSize('1200px');

    modal.afterClosed().subscribe((doc: Document) => {
      if (doc) {
        this.documents.push(doc);
      }
    });
  }

  download(doc: Document) {
    this.documentService.download(doc);
  }

  removeDocConfirm(doc: Document)  {
    const data = new DialogData();
    data.title = 'Enlever document';
    data.message = `Souhaitez-vous enlever ce document (${doc.filename}) de la page ?`;

    const modalConfirm = this.dialog.open(DialogValidationComponent, { data: data, disableClose: true});
    modalConfirm.updatePosition({top: '100px'});
    modalConfirm.afterClosed().subscribe(confirm => {
      if (confirm === true) {
        const idx = this.documents.findIndex((d) => {
          return d.id === doc.id;
        });

        if (idx > -1) {
          this.documents.splice(idx, 1);
        }

        // console.log(this.page.documents);
      }
    });
  }

  save() {
    const newPage: Page = {
      id: (this.page) ? this.page.id : null,
      title: this.pageForm.value.title,
      parent_id: this.pageForm.value.parent.id,
      icon: this.icon,
      content: {
        body: this.body
      },
      is_published: (this.page) ? this.page.is_published : true,
      is_favourite: (this.page) ? this.page.is_favourite : true,
      is_promoted: (this.page) ? this.page.is_promoted : true,
      documents: [],
      images: [],
      order: this.pageForm.value.order,
    };

    if (this.documents.length > 0) {
      this.chipsToDocumentsPage(newPage);
    }

    if (this.images.length > 0) {
      this.imagesToImagesPage(newPage);
    }

    console.log('page ', newPage);
    return this.pageService.updateOrCreate(newPage);
  }
}
